import { appApi, type AgreementConfigResult } from "@/api";
import { BRAND_NAME } from "@/config/brand";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type AgreementType = "service" | "privacy" | "payment";

const TYPE_CONFIG: Record<AgreementType, { title: string; icon: string; color: string }> = {
  service: { title: "用户服务协议", icon: "ri-file-text-line", color: "#FF6018" },
  privacy: { title: "隐私政策", icon: "ri-shield-user-line", color: "#17A87B" },
  payment: { title: "支付及订单规则", icon: "ri-bank-card-line", color: "#7A93E4" },
};

const normalizeType = (value: string | null): AgreementType =>
  value === "privacy" || value === "payment" ? value : "service";

export default function AgreementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = normalizeType(searchParams.get("type"));
  const [config, setConfig] = useState<AgreementConfigResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    appApi
      .getAgreementConfig("customer")
      .then((data) => {
        if (active) {
          setConfig(data);
        }
      })
      .catch(() => {
        if (active) {
          setConfig(null);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const documentData = useMemo(() => {
    if (type === "privacy") {
      return {
        ...TYPE_CONFIG.privacy,
        title: config?.privacy_title || TYPE_CONFIG.privacy.title,
        content: config?.privacy_content || config?.privacy || "",
      };
    }
    if (type === "payment") {
      return {
        ...TYPE_CONFIG.payment,
        title: config?.payment_title || TYPE_CONFIG.payment.title,
        content: config?.payment_content || "",
      };
    }
    return {
      ...TYPE_CONFIG.service,
      title: config?.service_title || TYPE_CONFIG.service.title,
      content: config?.service_content || config?.user || "",
    };
  }, [config, type]);

  return (
    <div className="customer-fixed-shell min-h-screen bg-[#F7F2EC] pb-8">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-[#F7F2EC] px-4 pb-3 pt-12">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white"
        >
          <i className="ri-arrow-left-s-line text-xl text-[#5A4A3F]" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-bold text-[#1A1A1A]">{documentData.title}</h1>
          <p className="mt-0.5 text-[11px] text-[#A99A8A]">{BRAND_NAME}</p>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white">
          <i className={`${documentData.icon} text-base`} style={{ color: documentData.color }} />
        </div>
      </div>

      <main className="mx-4 rounded-2xl bg-white px-4 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-[#A99A8A]">
            <i className="ri-loader-4-line mr-2 animate-spin" />
            加载中...
          </div>
        ) : documentData.content ? (
          <article
            className="prose prose-sm max-w-none text-sm leading-7 text-[#4A3B32]"
            dangerouslySetInnerHTML={{ __html: documentData.content }}
          />
        ) : (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF4EC]">
              <i className="ri-file-warning-line text-xl text-[#FF6018]" />
            </div>
            <p className="text-sm font-medium text-[#1A1A1A]">协议内容暂未配置</p>
            <p className="mt-1 text-xs text-[#A99A8A]">请联系平台客服补充后再查看</p>
          </div>
        )}
      </main>
    </div>
  );
}
